import clsx from 'clsx';
import Button from '@/shared/components/UI/Button';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import CheckIcon from '@/shared/icons/CheckIcon';
import {
  UPGRADE_MODAL_FEATURES_INTRO,
  UPGRADE_MODAL_CHECK_PURPLE,
} from './freeToStarterModalReferenceContent';

/**
 * Plan card for FreeToStarterModal — spacing, type, and colors use V3 design tokens
 * (see src/v3/styles/variables.css); values follow the internal upgrade-modal reference.
 */
const FreeToStarterPlanCard = ({
  tierType,
  planTitle,
  tagline,
  price,
  originalPrice,
  priceSuffix = '/month',
  features = [],
  /** Row index of the gated feature (purple emphasis); null = no row-level highlight */
  featuredRowIndex = null,
  showMostPopularBadge = false,
  isHighlighted = false,
  checkmarkClassName = 'text-v3-green-primary',
  buttonLabel,
  onButtonClick,
  buttonLoading = false,
}) => {
  const featuresIntro = UPGRADE_MODAL_FEATURES_INTRO[tierType] ?? 'Everything in Free, plus';

  return (
    <div className="relative flex min-w-0 flex-col w-full">
      <div
        className={clsx(
          'relative flex h-full min-h-0 flex-col rounded-v3-sm bg-white p-v3-sm',
          isHighlighted
            ? 'border-2 border-v3-purple-primary ring-[3px] ring-v3-purple-primary/5 ring-offset-0 ring-offset-white'
            : 'border border-v3-card-border'
        )}
      >
        {showMostPopularBadge ? (
          <span
            className="absolute left-1/2 top-[-9px] z-10 -translate-x-1/2 rounded-full bg-v3-purple-primary px-v3-xm py-v3-xxs text-center font-inter-variable text-v3-h11 font-semibold leading-tight text-white whitespace-nowrap"
            role="status"
          >
            Most Popular
          </span>
        ) : null}
        <p className="mb-v3-xxs font-inter-variable text-v3-h11 font-semibold uppercase tracking-wide text-v3-black-secondary">
          {planTitle}
        </p>

        <div className="mb-v3-xm flex flex-wrap items-baseline gap-v3-xxs">
          {originalPrice ? (
            <span className="mr-v3-xs text-v3-h10 font-semibold text-v3-black-placeholder line-through">
              {originalPrice}
            </span>
          ) : null}
          <span className="font-inter-variable text-v3-h3 font-bold leading-none tracking-tighter text-v3-black-max">
            {price}
          </span>
          <span className="font-inter-variable text-v3-h10 font-medium text-v3-black-secondary">
            {priceSuffix}
          </span>
        </div>

        <p className="mb-v3-sm min-h-[calc(var(--v3-ml)+var(--v3-xs))] overflow-hidden font-inter-variable text-v3-h10 leading-[1.4] text-v3-black-secondary">
          {tagline}
        </p>

        <Button
          variant={isHighlighted ? 'primary' : 'secondary'}
          size="sm"
          label={buttonLabel}
          className={clsx(
            'w-full justify-center !rounded-v3-sm !text-v3-h10 !font-semibold',
            isHighlighted &&
              '!border-v3-purple-primary !bg-v3-purple-primary !text-white hover:!border-v3-purple-dark hover:!bg-v3-purple-dark',
            !isHighlighted &&
              '!border-v3-black-card !bg-v3-black-card !text-v3-black-max hover:!border-v3-foreground-2 hover:!bg-v3-foreground-2'
          )}
          onClick={onButtonClick}
          loading={buttonLoading}
          loadingLabel="Loading…"
        />

        <div className="mb-v3-ms mt-v3-sm h-px bg-v3-black-card" />

        <p className="mb-v3-xs font-inter-variable text-v3-h11 font-semibold uppercase tracking-wide text-v3-black-placeholder">
          {featuresIntro.toUpperCase()}
        </p>

        <ul className="flex flex-col gap-v3-xs">
          {features.map((line, idx) => {
            const isFeaturedRow = featuredRowIndex != null && idx === featuredRowIndex;
            const rowCheckClass = isFeaturedRow ? UPGRADE_MODAL_CHECK_PURPLE : checkmarkClassName;
            return (
              <li
                key={idx}
                className="flex items-start gap-v3-xs font-inter-variable text-v3-h10 leading-[1.35]"
              >
                <IconWrapper size={16} className={clsx('mt-px shrink-0', rowCheckClass)}>
                  <CheckIcon width={14} height={14} className="block" />
                </IconWrapper>
                <span className={clsx('min-w-0', isFeaturedRow ? 'font-semibold text-v3-purple-dark' : 'font-medium text-v3-black-max')}>
                  {typeof line === 'string' ? (
                    line
                  ) : (
                    <>
                      {line.map((seg, j) => (
                        <span
                          key={j}
                          className={
                            seg.h
                              ? isFeaturedRow
                                ? 'font-semibold text-v3-purple-dark'
                                : 'font-semibold text-v3-black-max'
                              : undefined
                          }
                        >
                          {seg.t}
                        </span>
                      ))}
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default FreeToStarterPlanCard;
